import axios from "axios";
import { DEFAULT_USER_DISCOVER_PAGE_SIZE, MAX_INSTRUMENT_FILTERS, MAX_STYLE_FILTERS } from "../constants/search";
import { normalizeStyleTagList } from "../utils/styleTags";
import { normalizeLocationRadiusFilter } from "../utils/userSearchFilters";
import { normalizeUsernameForLookup } from "../utils/username";
import { resolveUserDisplayName, resolveUserUsername } from "../utils/userIdentity";
import {
    normalizeUserConnectionList,
    normalizeUserConnectionPage,
    withOptionalOffsetPagination,
} from "../utils/userConnectionList";
import { requireCanonicalUserId } from "./userIdRequestValidation";
import {
    normalizeGatewayBackedServiceBaseUrl,
    resolveGatewayBackedServiceBaseUrl,
} from "./serviceBaseUrl";

const USER_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_USER_SERVICE_URL);
export const DEFAULT_AXIOS_CONFIG = { withCredentials: true };

const resolveUserServiceRequestBaseUrl = (baseUrl) => (
    normalizeGatewayBackedServiceBaseUrl(baseUrl) || USER_SERVICE_URL
);

const buildUserFollowEndpoint = (userId, baseUrl = USER_SERVICE_URL) => {
    const normalizedUserId = requireCanonicalUserId(userId);
    return `${baseUrl}/api/users/${encodeURIComponent(normalizedUserId)}/followers/me`;
};

const buildUserFollowersEndpoint = (userId, baseUrl = USER_SERVICE_URL) => {
    const normalizedUserId = requireCanonicalUserId(userId);
    return `${baseUrl}/api/users/${encodeURIComponent(normalizedUserId)}/followers`;
};

const buildUserServiceConfig = (options = {}) => {
    const config = { ...DEFAULT_AXIOS_CONFIG };
    if (options.signal) {
        config.signal = options.signal;
    }
    return config;
};

const fetchUserById = async (userId) => {
    if (!userId) {
        return null;
    }
    const response = await axios.get(`${USER_SERVICE_URL}/api/users/${userId}`, DEFAULT_AXIOS_CONFIG);
    return normalizeSearchResultEntry(response.data);
};

const fetchCurrentUser = async () => {
    try {
        const response = await axios.get(`${USER_SERVICE_URL}/api/users/me`, DEFAULT_AXIOS_CONFIG);
        return normalizeSearchResultEntry(response.data);
    } catch (err) {
        throw err;
    }
};

const fetchUserByUsername = async (username) => {
    const normalizedUsername = normalizeUsernameForLookup(username);
    if (!normalizedUsername) {
        return null;
    }
    try {
        const response = await axios.get(
            `${USER_SERVICE_URL}/api/users/by-username/${encodeURIComponent(normalizedUsername)}`,
            DEFAULT_AXIOS_CONFIG
        );
        return normalizeSearchResultEntry(response.data);
    } catch (error) {
        if (error?.response?.status !== 404) {
            console.error('Failed to fetch user by username:', error);
            throw error;
        }
        return null;
    }
};

const fetchPublicUserByUsername = async (username) => {
    const normalizedUsername = normalizeUsernameForLookup(username);
    if (!normalizedUsername) {
        return null;
    }
    try {
        const response = await axios.get(
            `${USER_SERVICE_URL}/api/users/public/by-username/${encodeURIComponent(normalizedUsername)}`,
            DEFAULT_AXIOS_CONFIG
        );
        return normalizeSearchResultEntry(response.data);
    } catch (error) {
        if (error?.response?.status !== 404) {
            console.error('Failed to fetch public user by username:', error);
            throw error;
        }
        return null;
    }
};

const normalizeSearchResultEntry = (entry = {}) => {
    const baseEntry = stripUserEmailFields(entry);
    const id = baseEntry?.id ?? baseEntry?.userId ?? baseEntry?.user?.id;
    const username = resolveUserUsername(baseEntry);
    const displayName = resolveUserDisplayName(baseEntry);

    return {
        ...baseEntry,
        id,
        username,
        displayName,
    };
};

const stripUserEmailFields = (entry = {}) => {
    if (!entry || typeof entry !== "object") {
        return entry;
    }
    const { email: _email, user, ...rest } = entry;
    if (!user || typeof user !== "object") {
        return rest;
    }
    const { email: _nestedEmail, ...safeUser } = user;
    return {
        ...rest,
        user: safeUser,
    };
};

const normalizeSearchResultList = (data) => {
    const list = Array.isArray(data) ? data : [];
    return list.map((entry) => normalizeSearchResultEntry(entry));
};

const buildSearchPayload = (criteria = {}, options = {}) => {
    const payload = {};
    const setIfPresent = (key, value) => {
        if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed) {
                payload[key] = trimmed;
            }
        }
    };

    setIfPresent("query", criteria.query);
    setIfPresent("commitment", criteria.commitment);

    const locationRadiusFilter = normalizeLocationRadiusFilter(criteria);
    if (locationRadiusFilter) {
        payload.locationLatitude = locationRadiusFilter.latitude;
        payload.locationLongitude = locationRadiusFilter.longitude;
        payload.locationRadiusMiles = locationRadiusFilter.radiusMiles;
    } else {
        setIfPresent("location", criteria.location);
    }

    const preferredStyles = normalizeStyleTagList(criteria.preferredStyles).slice(0, MAX_STYLE_FILTERS);
    if (preferredStyles.length) {
        payload.preferredStyles = preferredStyles;
    } else {
        setIfPresent("preferredStyle", criteria.preferredStyle);
    }

    if (Array.isArray(criteria.instrumentExperience)) {
        const normalized = criteria.instrumentExperience
            .map((exp) => ({
                instrument: (exp?.instrument || "").trim(),
            }))
            .filter((exp) => exp.instrument)
            .slice(0, MAX_INSTRUMENT_FILTERS);
        if (normalized.length) {
            payload.instrumentExperience = normalized;
        }
    }

    if (criteria.limit && Number.isFinite(criteria.limit)) {
        const bounded = Math.min(Math.max(criteria.limit, 1), 50);
        payload.limit = bounded;
    }

    if (typeof options.cursor === "string" && options.cursor.trim()) {
        payload.cursor = options.cursor.trim();
    }

    if (options.includeTotal === true) {
        payload.includeTotal = true;
    }

    return payload;
};

const searchUsers = async (criteriaOrQuery = {}, options = {}) => {
    const baseUrl = typeof options === "string"
        ? resolveUserServiceRequestBaseUrl(options)
        : resolveUserServiceRequestBaseUrl(options.baseUrl);
    const searchOptions = typeof options === "string" ? {} : options;
    const criteria = typeof criteriaOrQuery === "string"
        ? { query: criteriaOrQuery }
        : (criteriaOrQuery || {});
    const payload = buildSearchPayload(criteria);
    if (Object.keys(payload).length === 0) {
        return [];
    }
    const config = { ...DEFAULT_AXIOS_CONFIG };
    if (searchOptions.signal) {
        config.signal = searchOptions.signal;
    }
    const response = await axios.post(`${baseUrl}/api/users/search`, payload, config);
    return normalizeSearchResultList(response?.data);
};

const searchUsersPage = async (criteriaOrQuery = {}, options = {}) => {
    const baseUrl = typeof options === "string"
        ? resolveUserServiceRequestBaseUrl(options)
        : resolveUserServiceRequestBaseUrl(options.baseUrl);
    const searchOptions = typeof options === "string" ? {} : options;
    const criteria = typeof criteriaOrQuery === "string"
        ? { query: criteriaOrQuery }
        : (criteriaOrQuery || {});
    const payload = buildSearchPayload(
        {
            ...criteria,
            limit: criteria.limit ?? DEFAULT_USER_DISCOVER_PAGE_SIZE,
        },
        {
            cursor: searchOptions.cursor,
            includeTotal: searchOptions.includeTotal,
        }
    );
    const config = { ...DEFAULT_AXIOS_CONFIG };
    if (searchOptions.signal) {
        config.signal = searchOptions.signal;
    }

    const response = await axios.post(`${baseUrl}/api/users/search/page`, payload, config);
    const page = response?.data ?? {};

    return {
        items: normalizeSearchResultList(page?.items),
        hasMore: Boolean(page?.hasMore),
        nextCursor: page?.nextCursor || null,
        pageSize: Number.isFinite(page?.pageSize) ? page.pageSize : 0,
        totalCount: Number.isFinite(page?.totalCount) ? page.totalCount : null,
    };
};

const getUserFollowStatus = async (userId, options = {}) => {
    const baseUrl = resolveUserServiceRequestBaseUrl(options.baseUrl);
    const config = buildUserServiceConfig(options);
    const response = await axios.get(buildUserFollowEndpoint(userId, baseUrl), config);
    return response.data;
};

const followUser = async (userId, options = {}) => {
    const baseUrl = resolveUserServiceRequestBaseUrl(options.baseUrl);
    const response = await axios.post(buildUserFollowEndpoint(userId, baseUrl), null, buildUserServiceConfig(options));
    return response.data;
};

const unfollowUser = async (userId, options = {}) => {
    const baseUrl = resolveUserServiceRequestBaseUrl(options.baseUrl);
    const response = await axios.delete(buildUserFollowEndpoint(userId, baseUrl), buildUserServiceConfig(options));
    return response.data;
};

const getMyFollowersPage = async (options = {}) => {
    const baseUrl = resolveUserServiceRequestBaseUrl(options.baseUrl);
    const response = await axios.get(
        withOptionalOffsetPagination(`${baseUrl}/api/users/me/followers`, options),
        buildUserServiceConfig(options)
    );
    return normalizeUserConnectionPage(response.data, options);
};

const getMyFollowers = async (options = {}) => {
    const page = await getMyFollowersPage(options);
    return normalizeUserConnectionList(page.items);
};

const getUserFollowersPage = async (userId, options = {}) => {
    const baseUrl = resolveUserServiceRequestBaseUrl(options.baseUrl);
    const response = await axios.get(
        withOptionalOffsetPagination(buildUserFollowersEndpoint(userId, baseUrl), options),
        buildUserServiceConfig(options)
    );
    return normalizeUserConnectionPage(response.data, options);
};

const getUserFollowers = async (userId, options = {}) => {
    const page = await getUserFollowersPage(userId, options);
    return normalizeUserConnectionList(page.items);
};

const userService = {
    fetchUserById,
    fetchCurrentUser,
    fetchUserByUsername,
    fetchPublicUserByUsername,
    searchUsers,
    searchUsersPage,
    getUserFollowStatus,
    followUser,
    unfollowUser,
    getUserFollowers,
    getUserFollowersPage,
    getMyFollowers,
    getMyFollowersPage,
};

export default userService;
